/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useRef, useState } from 'react';
import { useClaimStore } from '../../stores/claimStore';
import { useFormulaListStore } from '../../stores/formulaListStore';
import { getAccessToken } from '../../services/apiClient';
import { Tooltip } from '../layout/Tooltip';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export function ClaimBar() {
  const { myBatch, allClaims, isClaiming, error, claim, release, loadClaims, onClaimsChanged } = useClaimStore();
  const { setClaimedSetIds } = useFormulaListStore();
  const eventSourceRef = useRef<EventSource | null>(null);

  // Load active claims on mount + set up SSE listener for claim changes
  useEffect(() => {
    loadClaims().then(() => {
      // Restore formula filter if claim was recovered from server
      const batch = useClaimStore.getState().myBatch;
      if (batch && batch.formulaSetIds.length > 0) {
        setClaimedSetIds(batch.formulaSetIds);
      }
    });

    let es: EventSource | null = null;

    (async () => {
      try {
        const token = await getAccessToken();
        es = new EventSource(`${API_BASE}/events?token=${encodeURIComponent(token)}`, { withCredentials: true });
        eventSourceRef.current = es;

        es.addEventListener('claims-changed', () => {
          onClaimsChanged();
        });
      } catch {
        // SSE optional — claims still work via polling
      }
    })();

    return () => {
      if (es) es.close();
      eventSourceRef.current = null;
    };
  }, [loadClaims, onClaimsChanged]);

  const handleClaim = async () => {
    await claim(10); // Uses user's preferred batch size (default 10)
    const batch = useClaimStore.getState().myBatch;
    if (batch && batch.formulaSetIds.length > 0) {
      setClaimedSetIds(batch.formulaSetIds);
    }
  };

  const handleRelease = async () => {
    await release();
    setClaimedSetIds(null); // Clear filter, show full list
  };

  const otherClaims = allClaims.filter((c) => !myBatch || c.batchId !== myBatch.batchId);

  // Only show claim UI if user has an active batch OR other users have claims
  const shouldShowClaimBar = myBatch || otherClaims.length > 0;

  if (!shouldShowClaimBar) {
    return null;
  }

  return (
    <div className="mb-4 space-y-2">
      {/* Main claim bar */}
      {myBatch ? (
        <ClaimActiveBar
          formulaCount={myBatch.formulaSetIds.length}
          expiresAt={myBatch.expiresAt}
          onRelease={handleRelease}
        />
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
          <Tooltip text="Claim a batch of unassigned formulas to work on. Other pharmacists won't be able to assign them while you hold the claim.">
            <button
              onClick={handleClaim}
              disabled={isClaiming}
              className="bg-dds-blue-500 text-white text-sm px-4 py-1.5 rounded hover:bg-dds-blue-600 disabled:opacity-50"
            >
              {isClaiming ? 'Claiming...' : 'Claim Batch'}
            </button>
          </Tooltip>
          <span className="text-xs text-gray-500">
            Claim a batch of unassigned formulas to reserve them for your session
          </span>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">
          {error}
        </div>
      )}

      {/* Other users' active claims */}
      {otherClaims.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-700">
          <span className="font-medium">Active claims:</span>{' '}
          {otherClaims.map((c) => (
            <span key={c.batchId} className="inline-block mr-3">
              {c.claimedByName} ({c.formulaCount} formulas)
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Active claim bar with live countdown timer */
function ClaimActiveBar({ formulaCount, expiresAt, onRelease }: {
  formulaCount: number;
  expiresAt: string;
  onRelease: () => void;
}) {
  const [remaining, setRemaining] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    function tick() {
      const ms = new Date(expiresAt).getTime() - Date.now();
      if (ms <= 0) {
        setRemaining('Expired');
        setIsUrgent(true);
        return;
      }
      const totalSec = Math.floor(ms / 1000);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      setRemaining(`${min}:${sec.toString().padStart(2, '0')}`);
      setIsUrgent(min < 5);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const borderColor = isUrgent ? 'border-amber-300' : 'border-green-200';
  const bgColor = isUrgent ? 'bg-amber-50' : 'bg-green-50';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-3 flex items-center gap-3 flex-wrap`}>
      <span className="text-sm font-medium text-green-700">
        {formulaCount} formula{formulaCount > 1 ? 's' : ''} claimed
      </span>
      <span className={`text-sm font-mono tabular-nums ${isUrgent ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}>
        {remaining === 'Expired' ? '⏰ Expired' : `⏱ ${remaining}`}
      </span>
      <Tooltip text="Release your claimed formulas so other pharmacists can work on them">
        <button
          onClick={onRelease}
          className="text-sm text-red-600 hover:text-red-800 underline ml-auto"
        >
          Release
        </button>
      </Tooltip>
    </div>
  );
}
